import React, { useState, useEffect, useCallback } from "react";
import Lightbox from "react-18-image-lightbox";
import "react-18-image-lightbox/style.css";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import * as XLSX from "xlsx";

const IMAGE_EXTS = ["jpg", "jpeg", "png", "webp", "gif", "bmp", "svg", "avif"];
const SHEET_EXTS = ["xlsx", "xls", "csv"];
// PDFs and office docs both go through Google Docs viewer
const DOC_EXTS   = ["pdf", "doc", "docx", "ppt", "pptx"];

function getExtension(url) {
  try {
    return url.split("?")[0].split(".").pop().toLowerCase();
  } catch {
    return "";
  }
}

function getFileType(url) {
  if (!url) return "other";
  const ext = getExtension(url);
  if (IMAGE_EXTS.includes(ext))  return "image";
  if (SHEET_EXTS.includes(ext))  return "sheet";
  if (DOC_EXTS.includes(ext))    return "doc";   // includes pdf
  return "other";
}

function getFileName(url) {
  try {
    const raw = url.split("?")[0].split("/").pop();
    const dashIdx = raw.indexOf("-");
    return dashIdx !== -1 ? raw.slice(dashIdx + 1) : raw;
  } catch {
    return "Attachment";
  }
}


// ─── Sheet Viewer (XLSX / XLS / CSV) ─────────────────────────────────────────
const SheetViewer = ({ file }) => {
  const [html, setHtml]       = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);

    fetch(file)
      .then((res) => {
        if (!res.ok) throw new Error("Fetch failed");
        return res.arrayBuffer();
      })
      .then((buffer) => {
        if (cancelled) return;
        const workbook  = XLSX.read(buffer, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet     = workbook.Sheets[sheetName];
        const htmlStr   = XLSX.utils.sheet_to_html(sheet, { editable: false });
        setHtml(htmlStr);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [file]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading spreadsheet…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: "center", mt: 4 }}>
        <Typography>Unable to load spreadsheet.</Typography>
        <a href={file} target="_blank" rel="noreferrer" download
          style={{ color: "#1976d2", marginTop: 8, display: "inline-block" }}>
          Download instead
        </a>
      </Box>
    );
  }

  return (
    <Box sx={{ overflow: "auto", height: "100%", p: 1 }}>
      <style>{`
        .sheet-preview table { border-collapse: collapse; font-size: 13px; font-family: Arial, sans-serif; }
        .sheet-preview td, .sheet-preview th { border: 1px solid #d0d0d0; padding: 4px 8px; white-space: nowrap; }
        .sheet-preview tr:first-child td { background: #f0f0f0; font-weight: 600; }
      `}</style>
      <div className="sheet-preview" dangerouslySetInnerHTML={{ __html: html }} />
    </Box>
  );
};

// ─── Shared loading overlay ──────────────────────────────────────────────────
const LoadingOverlay = ({ label = "Loading…" }) => (
  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 2, color: "#666" }}>
    <CircularProgress size={36} sx={{ color: "#E27534" }} />
    <Typography sx={{ fontSize: 13 }}>{label}</Typography>
  </Box>
);

// ─── Google Docs Viewer (DOC / DOCX / PPT etc.) ──────────────────────────────
const DocViewer = ({ file }) => {
  const [loaded, setLoaded] = useState(false);
  const src = `https://docs.google.com/viewer?url=${encodeURIComponent(file)}&embedded=true`;
  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
      {!loaded && (
        <Box sx={{ position: "absolute", inset: 0, zIndex: 1, bgcolor: "#fff" }}>
          <LoadingOverlay label="Loading preview…" />
        </Box>
      )}
      <iframe
        key={file}
        src={src}
        title="document-preview"
        width="100%"
        height="100%"
        style={{ border: "none", display: "block" }}
        onLoad={() => setLoaded(true)}
      />
    </Box>
  );
};

// ─── Dialog wrapper shared by PDF / Sheet / Doc / Other ──────────────────────
const PreviewDialog = ({ file, fileType, onClose, onPrev, onNext, hasPrev, hasNext, totalCount, currentIdx }) => {
  const fileName = getFileName(file);

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: "90vh", display: "flex", flexDirection: "column" } }}
      sx={{ zIndex: 2000 }}
    >
      <DialogTitle sx={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        py: 1, px: 2, borderBottom: "1px solid #e0e0e0", flexShrink: 0,
      }}>
        <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, maxWidth: "60%" }}>
          {fileName}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
          {totalCount > 1 && (
            <>
              <Tooltip title="Previous">
                <span>
                  <IconButton size="small" onClick={onPrev} disabled={!hasPrev}>
                    <ChevronLeftIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Typography sx={{ fontSize: 13, color: "#666", mx: 0.5 }}>
                {currentIdx + 1} / {totalCount}
              </Typography>
              <Tooltip title="Next">
                <span>
                  <IconButton size="small" onClick={onNext} disabled={!hasNext}>
                    <ChevronRightIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
          <Tooltip title="Download">
            <IconButton size="small" component="a" href={file} target="_blank" rel="noreferrer" download>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {fileType === "sheet" && <SheetViewer file={file} />}
        {fileType === "doc"   && <DocViewer   file={file} />}
        {fileType === "other" && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 2 }}>
            <Typography color="text.secondary">Preview not available for this file type.</Typography>
            <a href={file} target="_blank" rel="noreferrer" download
              style={{ padding: "8px 20px", background: "#E27534", color: "white", borderRadius: 6, textDecoration: "none", fontWeight: 600 }}>
              Download File
            </a>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─── Main export ──────────────────────────────────────────────────────────────
const AttachmentViewer = ({ attachments, onClose, defaultIndex }) => {
  const [index, setIndex] = useState(defaultIndex ?? 0);

  if (!attachments?.length) return null;

  const currentFile = attachments[index];
  const fileType    = getFileType(currentFile);

  const goPrev = () => setIndex((i) => Math.max(0, i - 1));
  const goNext = () => setIndex((i) => Math.min(attachments.length - 1, i + 1));

  if (fileType === "image") {
    return (
      <Lightbox
        mainSrc={attachments[index]}
        nextSrc={attachments.length > 1 ? attachments[(index + 1) % attachments.length] : undefined}
        prevSrc={attachments.length > 1 ? attachments[(index + attachments.length - 1) % attachments.length] : undefined}
        onCloseRequest={onClose}
        onMovePrevRequest={goPrev}
        onMoveNextRequest={goNext}
        reactModalStyle={{ overlay: { zIndex: 2000 } }}
      />
    );
  }

  return (
    <PreviewDialog
      file={currentFile}
      fileType={fileType}
      onClose={onClose}
      onPrev={goPrev}
      onNext={goNext}
      hasPrev={index > 0}
      hasNext={index < attachments.length - 1}
      totalCount={attachments.length}
      currentIdx={index}
    />
  );
};

export default AttachmentViewer;
